import React, { useState, useEffect, useMemo } from "react";
import { Container } from "react-bootstrap";
import '../style.css';
import { useTranslation } from 'react-i18next';

const newGalleryImageUrls = (() => {
  const ctx = require.context('../assets/new', false, /\.(jpe?g|png|gif|webp)$/i);
  return ctx.keys().sort().map((key) => ctx(key));
})();

/**
 * Remove item at `targetOneBased`; place the image from `sourceOneBased` there;
 * drop that image from its old slot (net −1 length). Positions are 1-based.
 */
function replacePositionWithOther(urls, targetOneBased, sourceOneBased) {
  let i = targetOneBased - 1;
  let j = sourceOneBased - 1;
  if (urls.length <= Math.max(i, j) || i === j) return [...urls];
  const out = [...urls];
  const image = out[j];
  if (j > i) {
    out.splice(j, 1);
    out.splice(i, 1);
    out.splice(i, 0, image);
  } else {
    out.splice(j, 1);
    out.splice(i - 1, 1);
    out.splice(i - 1, 0, image);
  }
  return out;
}

const galleryImageUrls = (() => {
  let urls = [...newGalleryImageUrls];
  urls = replacePositionWithOther(urls, 3, 16);
  urls = replacePositionWithOther(urls, 4, 19);
  return urls;
})();

/** Collapsed grid shows this many items before "See more". */
const GALLERY_PREVIEW_COUNT = 12;

const Gallery = () => {
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  const { t } = useTranslation();

  const products = useMemo(
    () =>
      galleryImageUrls.map((img, index) => ({
        id: index + 1,
        img,
        name: `Product ${index + 1}`,
        desc: t('gallery.productDesc', { defaultValue: 'Premium quality stone product' }),
      })),
    [t]
  );

  const keyProducts = [
    { image: "./assets/coverstone.png" },
    { image: "./assets/headstone.png" },
    { image: "./assets/vases.png" },
  ];

  const handleNextMain = () => {
    setCarouselIndex((prevIndex) => (prevIndex + 1) % keyProducts.length);
  };

  const handlePrevMain = () => {
    setCarouselIndex((prevIndex) => (prevIndex - 1 + keyProducts.length) % keyProducts.length);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCarouselIndex((prevIndex) => (prevIndex + 1) % keyProducts.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [keyProducts.length]);

  return (
    <div className="gallery-container">
      {/* Hero Section */}
      <section className="gallery-hero">
        <Container>
          <div className="gallery-hero-content">
            <h1 className="gallery-hero-title">{t('gallery.title')}</h1>
            <p className="gallery-hero-subtitle">{t('gallery.subtitle')}</p>
          </div>
        </Container>
      </section>

      {/* Featured Carousel */}
      <section className="gallery-featured">
        <Container>
          <div className="carousel-wrapper">
            <button type="button" className="carousel-btn prev" onClick={handlePrevMain}>
              ‹
            </button>
            <div className="carousel-image-container">
              <img
                src={keyProducts[carouselIndex].image}
                alt="Featured Gallery Item"
                className="carousel-image"
              />
            </div>
            <button type="button" className="carousel-btn next" onClick={handleNextMain}>
              ›
            </button>
          </div>
        </Container>
      </section>

      {/* Products grid — single collection */}
      <section className="gallery-products-section">
        <Container>
          <div className="gallery-group-section">
            <div className="gallery-group-header">
              <h2 className="gallery-group-title">{t('gallery.collectionHeader')}</h2>
              <p className="gallery-group-subtitle">{t('gallery.collectionSubtitle')}</p>
            </div>

            <div className="gallery-products-grid">
              {(isExpanded ? products : products.slice(0, GALLERY_PREVIEW_COUNT)).map((product) => (
                <div key={product.id} className="gallery-product-card">
                  <div className="gallery-product-image-wrapper">
                    <img
                      src={product.img}
                      alt={product.name}
                      className="gallery-product-image"
                    />
                    <div className="gallery-product-overlay">
                      <button type="button" className="gallery-product-btn">{t('gallery.buttonViewDetails')}</button>
                    </div>
                  </div>
                  <div className="gallery-product-info">
                    <h3 className="gallery-product-name">{product.name}</h3>
                    <p className="gallery-product-desc">{product.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {products.length > GALLERY_PREVIEW_COUNT && (
              <div className="gallery-group-action">
                <button
                  type="button"
                  className="see-more-btn"
                  onClick={() => setIsExpanded((v) => !v)}
                >
                  {isExpanded ? t('gallery.seeLess', 'See Less') : t('gallery.seeMore', 'See More')}
                  <span className="arrow-icon">{isExpanded ? "‹" : "›"}</span>
                </button>
              </div>
            )}
          </div>
        </Container>
      </section>
    </div>
  );
};

export default Gallery;